import { useEffect, useState } from 'react'
import { supabase } from '../../api/supabase'
import CustomerCombobox from '../CustomerCombobox'
import WarningAckList from './WarningAckList'
import { allAcked, createSupplierInvoice, detectWarnings, r2, toAckInputs, type AckState, type Warning } from '../../lib/supplierInvoices'

const CURRENCIES = ['GBP', 'USD', 'EUR', 'CNY']

interface Props { onClose: () => void; onCreated: (invoiceId: string) => void }

export default function NewSupplierInvoiceDialog({ onClose, onCreated }: Props) {
  const [supplierId, setSupplierId] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10))
  const [currency, setCurrency] = useState('GBP')
  const [exchRate, setExchRate] = useState('1')
  const [net, setNet] = useState('')
  const [vat, setVat] = useState('0')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [warnings, setWarnings] = useState<Warning[]>([])
  const [acks, setAcks] = useState<AckState>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const netN = parseFloat(net) || 0
  const vatN = parseFloat(vat) || 0
  const rate = currency === 'GBP' ? 1 : parseFloat(exchRate) || 0

  // Live duplicate-number check (same supplier + same invoice number).
  useEffect(() => {
    if (!supplierId || !invoiceNo.trim()) { setWarnings([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      detectWarnings({ supplierId, invoiceNo, currency, exchRate: rate || 1, invoiceId: null, allocs: [] })
        .then(r => { if (!cancelled) setWarnings(r.warnings) })
        .catch(() => { if (!cancelled) setWarnings([]) })
    }, 400)
    return () => { cancelled = true; clearTimeout(t) }
  }, [supplierId, invoiceNo, currency, rate])

  const valid = !!supplierId && invoiceNo.trim() !== '' && !!invoiceDate && net !== '' && netN >= 0 && vatN >= 0 && rate > 0
  const canSave = valid && allAcked(warnings, acks) && !saving

  async function handleSave() {
    setSaving(true); setError(null)
    try {
      let attachment_path: string | null = null
      if (file) {
        attachment_path = `${crypto.randomUUID()}/${file.name.replace(/[^A-Za-z0-9._-]/g, '_')}`
        const up = await supabase.storage.from('supplier-invoices').upload(attachment_path, file)
        if (up.error) throw new Error(`Attachment upload failed: ${up.error.message}`)
      }
      const res = await createSupplierInvoice({
        supplier_id: supplierId, invoice_no: invoiceNo.trim(), invoice_date: invoiceDate, currency,
        exch_rate: rate, net_amount: netN, vat_amount: vatN, total_amount: r2(netN + vatN), notes, attachment_path,
      }, toAckInputs(warnings, acks))
      if (!res.ok) {
        if (res.warnings) { setWarnings(res.warnings); setError('A warning changed while saving -- please review and tick again.') }
        else setError(res.message || res.error)
        return
      }
      onCreated(res.invoice_id)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white'
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
  const hasStrong = warnings.some(w => w.severity === 'strong')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New supplier invoice</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Enter the invoice as received. Allocate it to job costs afterwards.</p>
        </div>
        <div className="space-y-4 overflow-y-auto px-6 py-4">
          <div>
            <label className={label}>Supplier *</label>
            <CustomerCombobox value={supplierId} onChange={setSupplierId} anyRole placeholder="Search supplier…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Invoice number *</label><input className={input} value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} /></div>
            <div><label className={label}>Invoice date *</label><input type="date" className={input} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} /></div>
          </div>
          <WarningAckList warnings={warnings} state={acks} onChange={setAcks} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Currency</label>
              <select className={input} value={currency} onChange={e => { setCurrency(e.target.value); if (e.target.value === 'GBP') setExchRate('1') }}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Exchange rate (1 {currency} = ? GBP)</label>
              <input type="number" step="0.000001" min="0" className={input} value={currency === 'GBP' ? '1' : exchRate}
                disabled={currency === 'GBP'} onChange={e => setExchRate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Net *</label><input type="number" step="0.01" min="0" className={input} value={net} onChange={e => setNet(e.target.value)} /></div>
            <div><label className={label}>VAT</label><input type="number" step="0.01" min="0" className={input} value={vat} onChange={e => setVat(e.target.value)} /></div>
            <div><label className={label}>Total</label><input className={`${input} bg-gray-50 dark:bg-gray-900`} readOnly value={r2(netN + vatN).toFixed(2)} /></div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Allocation to job costs uses the <strong>net</strong> amount (VAT is recoverable, so it is not a cost).</p>
          <div><label className={label}>Notes</label><textarea className={input} rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <div><label className={label}>Attachment (PDF / image)</label><input type="file" accept=".pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} className="text-sm" /></div>
          {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${hasStrong ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {saving ? 'Saving…' : warnings.length > 0 ? 'Confirm and save' : 'Save invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}
